import { useCallback, useEffect, useMemo, useState } from 'react';

type UseTableRowSelectionParams<TId extends string | number> = {
  itemIds: TId[];
  onSelectionChange?: (selectedIds: TId[]) => void;
  resetOnItemIdsChange?: boolean;
};

function serializeItemIds<TId extends string | number>(itemIds: TId[]): string {
  return itemIds.join('\0');
}

export function useTableRowSelection<TId extends string | number>({
  itemIds,
  onSelectionChange,
  resetOnItemIdsChange = false,
}: UseTableRowSelectionParams<TId>) {
  const [selectedIds, setSelectedIds] = useState<Set<TId>>(new Set());
  const itemIdsKey = useMemo(() => serializeItemIds(itemIds), [itemIds]);

  const allSelected = itemIds.length > 0 && selectedIds.size === itemIds.length;
  const hasSelectedItems = selectedIds.size > 0;

  useEffect(() => {
    if (resetOnItemIdsChange) {
      setSelectedIds(new Set());
    }
  }, [itemIdsKey, resetOnItemIdsChange]);

  useEffect(() => {
    onSelectionChange?.(Array.from(selectedIds));
  }, [onSelectionChange, selectedIds]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(itemIds));
  }, [allSelected, itemIds]);

  const handleSelectItem = useCallback((id: TId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((id: TId) => selectedIds.has(id), [selectedIds]);

  const getSelectedItems = useCallback(
    <T extends { id: TId }>(items: T[]) => items.filter((item) => selectedIds.has(item.id)),
    [selectedIds],
  );

  return {
    selectedIds,
    allSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectItem,
    clearSelection,
    isSelected,
    getSelectedItems,
  };
}
